import { useState, type CSSProperties, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api.js';
import { Layout } from '../components/Layout.js';
import { Icon, type IconName } from '../components/ui/Icon.js';
import { Button } from '../components/ui/Button.js';
import { useLogout, useLogoutAll, useProfile } from '../hooks/useAuth.js';

function getCsrf(): string {
  const m = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]*)/);
  return m ? decodeURIComponent(m[1]) : '';
}

function initials(name?: string | null): string {
  if (!name) return '··';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

type TotpSetupState =
  | { phase: 'idle' }
  | { phase: 'scanning'; qrDataUrl: string; secret: string }
  | { phase: 'confirming'; qrDataUrl: string; secret: string }
  | { phase: 'done' };

const inputStyle: CSSProperties = {
  width: '100%',
  background: 'var(--surface-2)',
  border: '1px solid var(--line-soft)',
  borderRadius: 12,
  padding: '10px 14px',
  fontSize: 14,
  color: 'var(--ink)',
  fontFamily: 'inherit',
  outline: 'none',
};

const dangerButtonStyle: CSSProperties = {
  padding: '10px 16px',
  fontSize: 13.5,
  fontWeight: 500,
  borderRadius: 12,
  border: '1px solid color-mix(in oklab, var(--danger) 40%, var(--line-soft))',
  background: 'color-mix(in oklab, var(--danger) 12%, var(--surface))',
  color: 'var(--danger)',
  cursor: 'pointer',
  fontFamily: 'inherit',
};

function SectionCard({ icon, title, blurb, children }: {
  icon: IconName; title: string; blurb?: string; children: ReactNode;
}) {
  return (
    <div className="card p-6 mb-6" style={{ maxWidth: 640 }}>
      <div className="flex items-center gap-2 mb-1" style={{ color: 'var(--ink-3)' }}>
        <Icon name={icon} size={14} />
        <span className="text-xs font-semibold uppercase" style={{ letterSpacing: '0.12em' }}>{title}</span>
      </div>
      {blurb && <p className="text-sm mb-4" style={{ color: 'var(--ink-3)' }}>{blurb}</p>}
      {children}
    </div>
  );
}

function IdentitySection() {
  const { data: profile } = useProfile();
  const name = profile?.name ?? '';
  const email = profile?.email ?? '';

  return (
    <div className="card p-6 mb-6" style={{ maxWidth: 640 }}>
      <div className="flex items-center gap-2 mb-4" style={{ color: 'var(--ink-3)' }}>
        <Icon name="user" size={14} />
        <span className="text-xs font-semibold uppercase" style={{ letterSpacing: '0.12em' }}>Identity</span>
      </div>

      <div className="flex items-center gap-4 mb-5">
        <div
          className="rounded-full flex items-center justify-center text-base font-bold flex-shrink-0"
          style={{ width: 56, height: 56, background: 'var(--accent)', color: 'var(--accent-ink)' }}
          aria-hidden
        >
          {initials(name)}
        </div>
        <div className="min-w-0">
          <p style={{ color: 'var(--ink)', fontSize: 16, fontWeight: 600 }}>{name || '—'}</p>
          <p className="truncate" style={{ color: 'var(--ink-3)', fontSize: 13 }}>{email || '—'}</p>
        </div>
      </div>

      <div className="space-y-3">
        <div>
          <label className="block mb-1" style={{ color: 'var(--ink-2)', fontSize: 13 }}>Display name</label>
          <input type="text" value={name} readOnly style={{ ...inputStyle, opacity: 0.7 }} />
        </div>
        <div>
          <label className="block mb-1" style={{ color: 'var(--ink-2)', fontSize: 13 }}>Email</label>
          <input type="email" value={email} readOnly style={{ ...inputStyle, opacity: 0.7 }} />
        </div>
        <p className="text-xs" style={{ color: 'var(--ink-4)' }}>
          To change your name or email, contact support.
        </p>
      </div>
    </div>
  );
}

function TwoFactorSection() {
  const qc = useQueryClient();
  const [setupState, setSetupState] = useState<TotpSetupState>({ phase: 'idle' });
  const [enableCode, setEnableCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const { data: status, isLoading } = useQuery<{ enabled: boolean }>({
    queryKey: ['totp-status'],
    queryFn: () => api.get('/api/auth/totp/status'),
  });

  const setup = useMutation({
    mutationFn: () => api.post<{ qrDataUrl: string; secret: string }>('/api/auth/totp/setup'),
    onSuccess: (data) => { setSetupState({ phase: 'scanning', qrDataUrl: data.qrDataUrl, secret: data.secret }); setError(''); },
    onError: (err: any) => setError(err.body?.error ?? 'Setup failed.'),
  });

  const enable = useMutation({
    mutationFn: (code: string) => api.post('/api/auth/totp/enable', { code }),
    onSuccess: () => { setSetupState({ phase: 'done' }); setEnableCode(''); setMessage('Two-factor authentication is now active.'); qc.invalidateQueries({ queryKey: ['totp-status'] }); },
    onError: (err: any) => setError(err.body?.error ?? 'Incorrect code.'),
  });

  const disable = useMutation({
    mutationFn: (code: string) => api.post('/api/auth/totp/disable', { code }),
    onSuccess: () => { setDisableCode(''); setMessage('Two-factor authentication has been disabled.'); setError(''); qc.invalidateQueries({ queryKey: ['totp-status'] }); },
    onError: (err: any) => setError(err.body?.error ?? 'Incorrect code.'),
  });

  if (isLoading) return null;
  const enabled = status?.enabled ?? false;

  return (
    <SectionCard
      icon="shield"
      title="Two-factor authentication"
      blurb={enabled
        ? 'Your account is protected with an authenticator app.'
        : 'Add an extra layer of security using Google Authenticator or any TOTP app.'}
    >
      {message && <p className="text-sm mb-3 inline-flex items-center gap-1.5" style={{ color: 'var(--accent)' }}>
        <Icon name="check" size={14} stroke={2.4} />
        {message}
      </p>}
      {error && <p className="text-sm mb-3" style={{ color: 'var(--danger)' }}>{error}</p>}

      {!enabled && setupState.phase === 'idle' && (
        <Button onClick={() => { setError(''); setMessage(''); setup.mutate(); }} disabled={setup.isPending}>
          {setup.isPending ? 'Generating…' : 'Set up 2FA'}
        </Button>
      )}

      {!enabled && (setupState.phase === 'scanning' || setupState.phase === 'confirming') && (
        <div className="space-y-4">
          <div className="flex flex-col items-center gap-3 p-4 rounded-xl"
            style={{ background: 'var(--surface-2)', border: '1px solid var(--line-soft)' }}>
            <img src={setupState.qrDataUrl} alt="2FA QR code"
              className="w-48 h-48 rounded-lg"
              style={{ background: 'white', padding: 8 }} />
            <p className="text-xs text-center" style={{ color: 'var(--ink-3)' }}>
              Scan with Google Authenticator, Authy, or any TOTP app.
            </p>
            <details className="text-xs cursor-pointer" style={{ color: 'var(--ink-4)' }}>
              <summary>Can't scan? Show backup key</summary>
              <code className="block mt-2 break-all px-2 py-1 rounded select-all"
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 11,
                  background: 'var(--surface-3)',
                  color: 'var(--ink-2)',
                }}>
                {setupState.secret}
              </code>
            </details>
          </div>
          <p className="text-sm font-medium" style={{ color: 'var(--ink)' }}>Enter the 6-digit code to confirm setup:</p>
          <form onSubmit={(e: FormEvent) => { e.preventDefault(); setError(''); enable.mutate(enableCode); }} className="flex gap-2">
            <input
              type="text" inputMode="numeric" pattern="\d{6}" maxLength={6} autoComplete="one-time-code" required
              value={enableCode} onChange={(e) => setEnableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              style={{
                ...inputStyle,
                flex: 1,
                fontFamily: 'var(--font-mono)',
                letterSpacing: '0.4em',
                textAlign: 'center',
                fontSize: 16,
              }}
            />
            <Button type="submit" disabled={enable.isPending || enableCode.length !== 6}>
              {enable.isPending ? 'Verifying…' : 'Activate'}
            </Button>
          </form>
          <button type="button" onClick={() => { setSetupState({ phase: 'idle' }); setError(''); }}
            className="text-sm hover:underline" style={{ color: 'var(--ink-4)' }}>
            Cancel
          </button>
        </div>
      )}

      {enabled && (
        <form onSubmit={(e: FormEvent) => { e.preventDefault(); setError(''); setMessage(''); disable.mutate(disableCode); }} className="space-y-3">
          <p className="text-sm" style={{ color: 'var(--ink-3)' }}>To disable 2FA, enter your current authenticator code:</p>
          <div className="flex gap-2">
            <input
              type="text" inputMode="numeric" pattern="\d{6}" maxLength={6} autoComplete="one-time-code" required
              value={disableCode} onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              style={{
                ...inputStyle,
                flex: 1,
                fontFamily: 'var(--font-mono)',
                letterSpacing: '0.4em',
                textAlign: 'center',
                fontSize: 16,
              }}
            />
            <button type="submit" disabled={disable.isPending || disableCode.length !== 6}
              style={{ ...dangerButtonStyle, opacity: (disable.isPending || disableCode.length !== 6) ? 0.5 : 1 }}>
              {disable.isPending ? 'Disabling…' : 'Disable 2FA'}
            </button>
          </div>
        </form>
      )}
    </SectionCard>
  );
}

function ChangePasswordSection() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword]         = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwError, setPwError]                 = useState('');
  const [pwSuccess, setPwSuccess]             = useState('');

  const changePassword = useMutation({
    mutationFn: (data: { currentPassword: string; newPassword: string }) => api.post('/api/auth/change-password', data),
    onSuccess: () => { setPwSuccess('Password changed. You have been signed out of all devices.'); setPwError(''); setCurrentPassword(''); setNewPassword(''); setConfirmPassword(''); },
    onError: (err: any) => { setPwError(err.body?.error ?? 'Failed to change password.'); setPwSuccess(''); },
  });

  function handlePasswordChange(e: FormEvent) {
    e.preventDefault();
    setPwError('');
    if (newPassword !== confirmPassword) { setPwError('New passwords do not match.'); return; }
    if (newPassword.length < 12)         { setPwError('New password must be at least 12 characters.'); return; }
    changePassword.mutate({ currentPassword, newPassword });
  }

  return (
    <SectionCard icon="user" title="Change password">
      <form onSubmit={handlePasswordChange} className="space-y-3">
        {[
          { label: 'Current password',      val: currentPassword, set: setCurrentPassword, ac: 'current-password' },
          { label: 'New password',          val: newPassword,     set: setNewPassword,     ac: 'new-password' },
          { label: 'Confirm new password',  val: confirmPassword, set: setConfirmPassword, ac: 'new-password' },
        ].map(({ label, val, set, ac }) => (
          <div key={label}>
            <label className="block mb-1" style={{ color: 'var(--ink-2)', fontSize: 13 }}>{label}</label>
            <input type="password" autoComplete={ac} required value={val}
              onChange={(e) => set(e.target.value)} style={inputStyle} />
          </div>
        ))}
        {pwError   && <p className="text-sm" style={{ color: 'var(--danger)' }}>{pwError}</p>}
        {pwSuccess && (
          <p className="text-sm inline-flex items-center gap-1.5" style={{ color: 'var(--accent)' }}>
            <Icon name="check" size={14} stroke={2.4} />
            {pwSuccess}
          </p>
        )}
        <Button type="submit" disabled={changePassword.isPending}>
          {changePassword.isPending ? 'Saving…' : 'Update password'}
        </Button>
      </form>
    </SectionCard>
  );
}

function SessionsSection() {
  const logout = useLogout();
  const logoutAll = useLogoutAll();
  return (
    <SectionCard
      icon="bolt"
      title="Sessions"
      blurb="Sign out of this device or all devices and browsers."
    >
      <div className="space-y-3">
        <button
          onClick={() => logout.mutate()}
          disabled={logout.isPending}
          style={{
            padding: '10px 16px',
            fontSize: 13.5,
            fontWeight: 500,
            borderRadius: 12,
            border: '1px solid var(--line-soft)',
            background: 'var(--surface-2)',
            color: 'var(--ink)',
            cursor: 'pointer',
            opacity: logout.isPending ? 0.5 : 1,
            fontFamily: 'inherit',
            display: 'block',
            width: '100%',
          }}
        >
          {logout.isPending ? 'Signing out…' : 'Sign out this device'}
        </button>
        <button
          onClick={() => logoutAll.mutate()}
          disabled={logoutAll.isPending}
          style={{ ...dangerButtonStyle, opacity: logoutAll.isPending ? 0.5 : 1, display: 'block', width: '100%' }}
        >
          {logoutAll.isPending ? 'Signing out…' : 'Sign out all devices'}
        </button>
      </div>
    </SectionCard>
  );
}

function AccountSection() {
  const [deletePhase, setDeletePhase] = useState<'idle' | 'confirming'>('idle');
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  async function handleExport() {
    setExportLoading(true);
    try {
      const res = await fetch('/api/auth/export', { credentials: 'include' });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'platterplan-data-export.json';
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExportLoading(false);
    }
  }

  async function handleDelete(e: FormEvent) {
    e.preventDefault();
    setDeleteError('');
    setDeleteLoading(true);
    try {
      const res = await fetch('/api/auth/account', {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrf() },
        body: JSON.stringify({ password: deletePassword }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setDeleteError(body.error ?? 'Deletion failed. Check your password and try again.');
        return;
      }
      window.location.href = '/';
    } catch {
      setDeleteError('Network error. Please try again.');
    } finally {
      setDeleteLoading(false);
    }
  }

  return (
    <SectionCard
      icon="download"
      title="Data & privacy"
      blurb="Download a copy of your data or permanently delete your account."
    >
      <button
        onClick={handleExport}
        disabled={exportLoading}
        className="inline-flex items-center gap-2 mb-5"
        style={{
          padding: '10px 16px',
          fontSize: 13.5,
          fontWeight: 500,
          borderRadius: 12,
          border: '1px solid var(--line-soft)',
          background: 'var(--surface-2)',
          color: 'var(--ink)',
          cursor: 'pointer',
          opacity: exportLoading ? 0.5 : 1,
          fontFamily: 'inherit',
        }}
      >
        <Icon name="download" size={14} stroke={2} />
        {exportLoading ? 'Preparing…' : 'Download my data'}
      </button>

      <div style={{ height: 1, background: 'var(--line-soft)', marginBottom: 20 }} />

      {deletePhase === 'idle' ? (
        <button onClick={() => setDeletePhase('confirming')} style={dangerButtonStyle}>
          <Icon name="trash" size={14} style={{ display: 'inline', marginRight: 6, marginBottom: -2 }} />
          Delete my account
        </button>
      ) : (
        <form onSubmit={handleDelete} className="space-y-3">
          <div className="rounded-xl p-3"
            style={{
              background: 'color-mix(in oklab, var(--danger) 10%, var(--surface))',
              border: '1px solid color-mix(in oklab, var(--danger) 35%, var(--line-soft))',
            }}>
            <p className="text-sm font-medium mb-1" style={{ color: 'var(--danger)' }}>
              This cannot be undone
            </p>
            <p className="text-xs" style={{ color: 'color-mix(in oklab, var(--danger) 75%, var(--ink-2))' }}>
              All your meal plans, favorites, and settings will be permanently deleted.
            </p>
          </div>
          <div>
            <label className="block mb-1" style={{ color: 'var(--ink-2)', fontSize: 13 }}>Confirm with your password</label>
            <input type="password" autoComplete="current-password" required
              value={deletePassword} onChange={(e) => setDeletePassword(e.target.value)}
              style={inputStyle} />
          </div>
          {deleteError && <p className="text-sm" style={{ color: 'var(--danger)' }}>{deleteError}</p>}
          <div className="flex gap-2">
            <button type="submit" disabled={deleteLoading || !deletePassword}
              style={{
                padding: '10px 16px',
                fontSize: 13.5,
                fontWeight: 500,
                borderRadius: 12,
                border: 'none',
                background: 'var(--danger)',
                color: 'white',
                cursor: 'pointer',
                opacity: (deleteLoading || !deletePassword) ? 0.5 : 1,
                fontFamily: 'inherit',
              }}>
              {deleteLoading ? 'Deleting…' : 'Delete my account'}
            </button>
            <button type="button"
              onClick={() => { setDeletePhase('idle'); setDeletePassword(''); setDeleteError(''); }}
              className="text-sm hover:underline"
              style={{ color: 'var(--ink-4)', padding: '10px 12px' }}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </SectionCard>
  );
}

export function ProfilePage() {
  return (
    <Layout>
      <div className="w-full mx-auto" style={{ maxWidth: 1280, padding: '32px 24px 56px' }}>

        {/* ── Header ─────────────────────────────────────────────────── */}
        <div className="mb-8" style={{ maxWidth: 720 }}>
          <div className="text-xs font-semibold uppercase tracking-wider mb-2"
            style={{ color: 'var(--accent)', letterSpacing: '0.14em' }}>
            <Icon name="user" size={12} style={{ display: 'inline', marginRight: 6, marginBottom: -1 }} />
            Profile
          </div>
          <h1 className="h-display" style={{ fontSize: 'clamp(36px, 6vw, 56px)', margin: 0, color: 'var(--ink)', lineHeight: 1.0 }}>
            Account &{' '}
            <span style={{ color: 'var(--accent)', fontStyle: 'italic', fontWeight: 400 }}>security.</span>
          </h1>
          <p className="text-sm mt-3" style={{ color: 'var(--ink-3)' }}>
            Who you are, how you sign in, and what we know about you.
          </p>
        </div>

        <IdentitySection />
        <TwoFactorSection />
        <ChangePasswordSection />
        <SessionsSection />
        <AccountSection />
      </div>
    </Layout>
  );
}
